import { type BulkRequestRecordEdit, type SaveCallback, editableProps } from "./useChangeOverlay";
import { Form } from "antd";
import { PaymentForm } from "../../NewPaymentModal/PaymentForm/PaymentForm";
import { type StrictlyBasisPoints, basisPointsFromDollars, dollarsFromBasisPoints } from "@vyne-shared/utils/currency";
import { VyneModal } from "@vyne-shared/components/vyneModal/VyneModal";
import { antdModalV5, create, useModal } from "@ebay/nice-modal-react";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";
import type { FormInstance } from "antd";
import type { PatientUpdateForm, PatientUpdateRequest } from "api/requests";

/**
 * Convert a Bulk Request record to the existing patient format used for the new payment request form. This allows us to
 * reuse the form without changing it.
 * @param record The Bulk Request record we are editing.
 * @returns The translated form of the record to display on the data entry form. This is a combination of
 * ExistingPatient and PatientUpdateRequest, serving as data for the UI and initial values for the form.
 */
function translatePatient(record: BulkRequestRecord) {
    return {
        ...record,
        // default "already requested" balance to 0
        balance: record.balance ?? (0 as StrictlyBasisPoints),
        // Override amount and convert units to what the input control uses
        amount: dollarsFromBasisPoints(record.amount),
        // UpdateDate is required for the data type but not used by the form, so we just make up a value
        updateDate: new Date().valueOf(),
        // Bulk Request currently only operates on guarantors
        isGuarantor: true,
        // Bulk Request are always PMS patients so they can't be unlinked
        unlinked: false,
    };
}

/**
 * Build the request containing the user's changes.
 * @param form The form to obtain data from.
 * @returns The request containing the user's changes, or undefined if the form is not valid.
 */
async function buildRequest(form: FormInstance<PatientUpdateForm>) {
    let values: PatientUpdateForm;
    try {
        // Perform validation first - this may throw an error if fields are invalid, which is fine.
        values = await form.validateFields();
    } catch {
        // If validation fails, it will display error messages for the user, so we don't need to do anything else.
        return undefined;
    }

    return {
        // Start with all the form data
        ...values,
        // Have to convert units from the input type back to what we store in the Bulk Request record
        amount: basisPointsFromDollars(values.amount),
    } satisfies PatientUpdateRequest;
}

/**
 * Identify changes made to the Bulk Request record.
 * @param record The Bulk Request record we are changing.
 * @param request The user request to apply. Typed as `any` to avoid issues with indexing properties.
 * @returns List of changed properties.
 */
function getChanges(record: BulkRequestRecord, request: PatientUpdateRequest): BulkRequestRecordEdit {
    // NOTE: We have to account for any changes made in translatePatient or buildRequest. Fortunately, neither currently
    // changes any property names, we just add some properties we can throw away here.
    return Object.fromEntries(
        editableProps.filter((key) => request[key] !== record[key]).map((key) => [key, request[key]] as const),
    );
}

interface BulkRequestEditModalProps {
    readonly record: BulkRequestRecord;
    readonly save: SaveCallback;
}

export const BulkRequestEditModal = create<BulkRequestEditModalProps>(({ record, save }) => {
    const modal = useModal();

    // Note that we use PatientUpdateRequest for compatibility with the existing PaymentForm, but we translate data
    // points when saving anyway, so it doesn't matter that we don't use this type for Bulk Request.
    const [form] = Form.useForm<PatientUpdateForm>();

    const { onOk, ...antdProps } = antdModalV5(modal);
    const ourOnOk = async () => {
        const request = await buildRequest(form);
        if (request) {
            const changes = getChanges(record, request);
            save(record.pmsId, changes);

            onOk();
        }
    };

    return (
        <VyneModal {...antdProps} onOk={() => void ourOnOk()} title="Edit Bulk Request Record" okText="Save">
            <PaymentForm form={form} existingPrp={translatePatient(record)} keepAmount hideMessaging disableAllowComm />
        </VyneModal>
    );
});
